import { h } from '../dom';
import { api, type AppNotification, type Post, type User } from '../../api/client';
import { avatar, avatarStack, empty, photoImg, section, skeleton, timeAgo } from '../components';
import { refreshCounts, shell } from '../shell';
import { REACTION_EMOJI, parseReaction, reactionParts, reactionSentence } from '../reaction-text';

/** Plain sentence for a (merged) REACTION notification. */
export function reactionText(n: Pick<AppNotification, 'actorId' | 'actorName' | 'actorAvatar' | 'data'>) {
  return reactionSentence(parseReaction(n.data, { id: n.actorId, name: n.actorName ?? undefined, avatarUrl: n.actorAvatar }));
}

export function describe(n: AppNotification): { text: string; href: string } {
  const who = n.actorName ?? 'Ai đó';
  const post = `#/post/${n.data.postId ?? n.refId}`;
  switch (n.kind) {
    case 'COMMENT': return { text: `${who} bình luận: "${n.data.excerpt ?? n.data.preview ?? ''}"`, href: post };
    case 'REPLY': return { text: `${who} đã trả lời bình luận của bạn: "${n.data.preview ?? ''}"`, href: post };
    case 'FRIEND_POST': return { text: `${who} vừa đăng bộ ảnh mới`, href: post };
    case 'POST_SHARED': return { text: `${who} đã đăng ảnh có bạn`, href: post };
    case 'REACTION': return { text: reactionText(n), href: `${post}?reactors=1` };
    case 'FRIEND_REQUEST': return { text: `${who} muốn kết bạn với bạn`, href: '#/friends?tab=requests' };
    case 'FRIEND_ACCEPTED': return { text: `${who} đã đồng ý kết bạn ♡`, href: `#/u/${n.actorId}` };
    case 'ROOM_INVITE': return { text: `${who} mời bạn vào chụp chung${n.data.roomTitle ? ` · "${n.data.roomTitle}"` : ''}`, href: n.data.roomId ? `#/room/${n.data.roomId}` : '#/rooms' };
    case 'PHOTOS_READY': return { text: 'Bộ ảnh mới đã sẵn sàng trong thư viện 🎞', href: '#/library' };
    default: return { text: who, href: '#/feed' };
  }
}

export function renderNotifications(root: HTMLElement, _me: User) {
  const content = shell(root, { title: 'Thông báo', tab: '#/notifications' });
  content.append(section(async () => {
    const list = await api.notifications();
    if (list.some((n) => !n.readAt)) void api.markRead().then(() => refreshCounts(true));
    return list;
  }, (list) => list.length
    ? h('div.ulist', {}, ...list.map((n) => {
      if (n.kind === 'REACTION') return reactionRow(n);
      const d = describe(n);
      return h('a.urow.notif', { href: d.href, class: n.readAt ? '' : 'unread' },
        avatar(n.actorId ? { id: n.actorId, displayName: n.actorName ?? '?', avatarUrl: n.actorAvatar } : null, 42),
        h('div.umain', {}, h('span', {}, d.text), h('small', {}, timeAgo(n.createdAt))));
    }))
    : empty('🔔', 'Lời mời kết bạn, bình luận và ảnh mới sẽ hiện ở đây.', h('a.secondary.btn', { href: '#/rooms' }, '🎞 Rủ bạn chụp'), 'Chưa có thông báo'),
  () => skeleton('row', 6)));
}

// ------------------------------------------------------------------ merged reactions

/** Posts looked up for thumbnails, once per session. */
const postCache = new Map<string, Promise<Post | null>>();
const postOnce = (id: string) => {
  let p = postCache.get(id);
  if (!p) { p = api.post(id).catch(() => null); postCache.set(id, p); }
  return p;
};

/** Thumb of the post's strip, fetched only when the row scrolls into view. */
function postThumb(postId: string, thumbUrl?: string) {
  const box = h('span.rx-thumb', { 'aria-hidden': 'true' });
  if (thumbUrl) { box.append(h('img', { src: thumbUrl, alt: '', loading: 'lazy', decoding: 'async' })); return box; }
  const io = new IntersectionObserver((es) => {
    if (!es.some((e) => e.isIntersecting)) return;
    io.disconnect();
    void postOnce(postId).then((p) => {
      const ph = p?.photos.find((x) => x.kind === 'STRIP') ?? p?.photos[0];
      if (ph) box.replaceChildren(photoImg(ph, 'pimg'));
    });
  }, { rootMargin: '200px' });
  io.observe(box);
  return box;
}

function reactionRow(n: AppNotification) {
  const r = parseReaction(n.data, { id: n.actorId, name: n.actorName ?? undefined, avatarUrl: n.actorAvatar });
  const postId = n.data.postId ?? n.refId ?? '';
  const people = r.actors.map((a) => ({ id: a.id ?? a.name, displayName: a.name, avatarUrl: a.avatarUrl }));
  return h('a.urow.notif.rxnotif', { href: `#/post/${postId}?reactors=1`, class: n.readAt ? '' : 'unread', 'aria-label': reactionText(n) },
    h('span.rx-people', {}, people.length > 1 ? avatarStack(people, 30, 3) : avatar(people[0] ?? null, 42)),
    h('div.umain', {},
      h('span.rx-text', {}, ...reactionParts(r).map((p) => (p.bold ? h('b', {}, p.text) : p.text))),
      r.emojis.length ? h('span.rx-emojis', {}, ...r.emojis.map(([e, c]) => h('span.rx-e', {}, REACTION_EMOJI[e], ' ', h('b', {}, String(c))))) : null,
      h('small', {}, timeAgo(n.createdAt))),
    postThumb(postId, n.data.thumbUrl));
}
